import { RefObject } from 'react';
import { flushSync } from 'react-dom';
import { VariableSizeList } from 'react-window';

// The index of the row of this list that holds the element, or null.
export const listRowIndex = (
    container: Element | null,
    element: Element | null,
) => {
    const row = element?.closest<HTMLElement>('[data-row-index]');
    return row && container?.contains(row)
        ? Number(row.dataset.rowIndex)
        : null;
};

// Only rows that can take focus carry data-row-index. The row's own control
// comes first in document order, before the buttons inside it. It can be a
// link, as in the call history.
export const listRowTarget = (container: ParentNode | null, index: number) => {
    const row = `[data-row-index="${index}"]`;
    return (
        container?.querySelector<HTMLElement>(
            `${row} a[href], ${row} button, ${row} [tabindex]`,
        ) ?? null
    );
};

// The whole row scrolls into view before its control gets focus. Focus alone
// would scroll only the control, which can be smaller than the row. In a
// react-window list the row must also render first, as only the rows near
// the viewport are rendered.
export const focusListRow = (
    container: ParentNode | null,
    listRef: RefObject<VariableSizeList> | undefined,
    index: number,
) => {
    if (listRef) {
        flushSync(() => listRef.current?.scrollToItem(index));
    } else {
        container
            ?.querySelector(`[data-row-index="${index}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }
    const target = listRowTarget(container, index);
    target?.focus({ preventScroll: true });
    return target !== null;
};

// The panels of the menus, listboxes, comboboxes and popovers that triggers
// in this element have open. Their triggers point at them by aria-controls;
// a panel outside the element is an overlay, such as a row's "More options".
export const openOverlaysOf = (element: Element): Element[] =>
    Array.from(
        element.querySelectorAll('[aria-expanded="true"][aria-controls]'),
    )
        .map((trigger) =>
            document.getElementById(trigger.getAttribute('aria-controls')),
        )
        .filter((panel) => panel !== null && !element.contains(panel));
